import {
  FC,
  Suspense,
  lazy,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

import Dropzone from "@/components/common/Dropzone";
import { setLocalStorageItem, setPDF } from "@/utils/fileSystemHelpers";
import { validateInputFile } from "@/utils/merge";
import { AppContext } from "./context/AppContext";

const Alert = lazy(() => import("@/components/common/Alert"));
const AdblockAlert = lazy(() => import("@/components/common/AdblockAlert"));
const AdvertisingSection = lazy(
  () => import("@/components/conversion/footers/MergeFooter"),
);

interface MergePDFProps {
  onFinish?: () => void;
}

interface StoredMergeFile {
  name: string;
  path: string;
}

const MergePDF: FC<MergePDFProps> = ({ onFinish }) => {
  const [error, setError] = useState<string | null>(null);
  const { webAppUrl } = useContext(AppContext);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      try {
        acceptedFiles.forEach(validateInputFile);

        const storedFiles: StoredMergeFile[] = [];

        for (const file of acceptedFiles) {
          const value = {
            contents: new Uint8Array(await file.arrayBuffer()),
            mode: 33206,
            timestamp: new Date(),
          };
          const path = `/data/${uuidv4()}`;

          await setPDF(path, value);
          storedFiles.push({ name: file.name, path });
        }

        setLocalStorageItem("pdf_files", JSON.stringify(storedFiles));
        setLocalStorageItem("pdf_file", storedFiles[0].path);
        onFinish?.();

        window.location.href =
          webAppUrl ?? "https://mobisystems.com/mobipdf/online/merge";
      } catch (error: any) {
        setError(error.message);
      }
    },
    [webAppUrl],
  );

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const currentComponent = useMemo(
    () => (
      <Dropzone
        className="!tw-py-[40px]"
        onDrop={onDrop}
        multiple
        heading="pdfextra_merge_tool.dropzone.heading"
        chooseFileTranslation="pdfextra_merge_tool.dropzone.choose_files"
        dragFileTranslation="pdfextra_merge_tool.dropzone.drag_files_here"
        paragraphTranslation="pdfextra_organize_tool.dropzone.start_paragraph"
      />
    ),
    [onDrop],
  );

  return (
    <Suspense>
      <div className="tw-widget-root">
        <div className="tw-relative tw-bg-white tw-py-5 tw-p-5 sm:tw-p-[32px] tw-border-3 tw-border-ui-light-grey tw-rounded-t-[48px]">
          {currentComponent}
          <Alert
            message={error as string}
            show={error !== null}
            category="info"
            onClose={resetError}
          />
          <AdblockAlert />
        </div>
        <AdvertisingSection />
      </div>
    </Suspense>
  );
};

export default MergePDF;
